/**
 * Flatten the nested {@link WorkOrderDetailsApi} response into the flat
 * fields the master sequence prefill expects (route-stops shape). Returns a
 * {@link Partial} because BE may omit fields; callers merge the result into
 * the row they're constructing.
 */
import type { MasterStopApi } from 'types/api/route/masterStop'
import type { WorkOrderDetailsApi } from 'types/api/work-order/workOrderDetails'

type FlatLocationFields = Pick<
  MasterStopApi,
  | 'accountId'
  | 'accountName'
  | 'locationId'
  | 'addressName'
  | 'locationName'
  | 'line1'
  | 'unitNumber'
  | 'city'
  | 'state'
  | 'zipCode'
  | 'country'
  | 'latitude'
  | 'longitude'
  | 'nearestRoadLatitude'
  | 'nearestRoadLongitude'
>

/**
 * Subset of MasterStopApi fields that can be derived purely from
 * a WorkOrderDetailsApi response. Recurrence/measure/material come from the
 * configured-service detail endpoint (a separate adapter will cover that),
 * so they're not in here.
 */
export const toPendingMasterStopFields = (wo: WorkOrderDetailsApi): Partial<FlatLocationFields> => {
  const { account } = wo
  const { location } = wo
  const address = location?.address ?? null

  return {
    accountId: account?.id ?? undefined,
    accountName: account?.accountName ?? undefined,
    locationId: location?.id ?? undefined,
    // WOLV-4487 split: addressName is the address record's own label,
    // locationName the location's.
    addressName: address?.addressName ?? undefined,
    locationName: location?.locationName ?? undefined,
    line1: address?.line1 ?? undefined,
    unitNumber: address?.unitNumber ?? undefined,
    city: address?.city ?? undefined,
    state: address?.state ?? undefined,
    zipCode: address?.zipCode ?? undefined,
    country: address?.country ?? undefined,
    latitude: address?.latitude ?? undefined,
    longitude: address?.longitude ?? undefined,
    nearestRoadLatitude: address?.nearestRoadLatitude ?? undefined,
    nearestRoadLongitude: address?.nearestRoadLongitude ?? undefined,
  }
}
